import type {
  CreateWorkOrderInput,
  Page,
  RecordVerificationInput,
  RecordWorkOrderDetailsInput,
  WorkOrderApprovalReadiness,
  WorkOrderDetailsView,
  WorkOrderListItemView,
  WorkOrderListQuery,
  WorkOrderTransitionView,
  WorkOrderVerificationView,
  WorkOrderView,
} from '@chukua/common';
import { api } from '../../../../shared/shared.api';
import { toListParams } from '../../../../shared/policies/list-params';

export type { WorkOrderApprovalReadiness, WorkOrderDetailsView, WorkOrderListItemView, WorkOrderTransitionView, WorkOrderVerificationView, WorkOrderView };

/** The moves an order takes as `POST /work-orders/:id/<action>`. Verifying is its own resource, `/verifications`. */
export type WorkOrderMove = 'approve' | 'reject' | 'schedule' | 'start' | 'complete' | 'cancel';

/** `POST /work-orders/:id/verifications` answers the attempt and the order as it now stands. */
export interface RecordedVerification {
  verification: WorkOrderVerificationView;
  workOrder: WorkOrderView;
}

export function listWorkOrders(query: WorkOrderListQuery): Promise<Page<WorkOrderListItemView>> {
  return api<Page<WorkOrderListItemView>>(`/work-orders?${toListParams(query)}`);
}

export function getWorkOrder(id: string): Promise<WorkOrderView> {
  return api<WorkOrderView>(`/work-orders/${id}`);
}

export function listWorkOrderTransitions(id: string): Promise<WorkOrderTransitionView[]> {
  return api<WorkOrderTransitionView[]>(`/work-orders/${id}/transitions`);
}

export function getApprovalReadiness(id: string): Promise<WorkOrderApprovalReadiness> {
  return api<WorkOrderApprovalReadiness>(`/work-orders/${id}/approval-readiness`);
}

export function raiseWorkOrder(input: CreateWorkOrderInput): Promise<WorkOrderView> {
  return api<WorkOrderView>('/work-orders', { method: 'POST', body: JSON.stringify(input) });
}

/** `body` is `{ scheduledFor }` for a schedule and `{ reason }` for a reject or cancel; the other moves take none. */
export function moveWorkOrder(id: string, move: WorkOrderMove, body?: Record<string, string>): Promise<WorkOrderView> {
  return api<WorkOrderView>(`/work-orders/${id}/${move}`, { method: 'POST', body: body ? JSON.stringify(body) : undefined });
}

/** The newest recorded revision, or null when none was recorded yet. */
export function getWorkOrderDetails(id: string): Promise<WorkOrderDetailsView | null> {
  return api<WorkOrderDetailsView | null>(`/work-orders/${id}/details`);
}

export function recordWorkOrderDetails(id: string, input: RecordWorkOrderDetailsInput): Promise<WorkOrderDetailsView> {
  return api<WorkOrderDetailsView>(`/work-orders/${id}/details`, { method: 'POST', body: JSON.stringify(input) });
}

export function listVerifications(id: string): Promise<WorkOrderVerificationView[]> {
  return api<WorkOrderVerificationView[]>(`/work-orders/${id}/verifications`);
}

export function recordVerification(id: string, input: RecordVerificationInput): Promise<RecordedVerification> {
  return api<RecordedVerification>(`/work-orders/${id}/verifications`, { method: 'POST', body: JSON.stringify(input) });
}
